/**
 * TicketReplyForm - Form to add reply to ticket
 */

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { ChangeEvent, KeyboardEvent, useCallback, useRef, useState } from 'react';

interface TicketReplyFormProps {
  onSubmit: (content: string) => Promise<boolean>;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
}

export function TicketReplyForm({
  onSubmit,
  disabled = false,
  isLoading = false,
  placeholder = 'Type your reply...',
  className,
}: TicketReplyFormProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed || disabled || isLoading) return;

    const success = await onSubmit(trimmed);
    if (success) {
      setValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  }, [value, disabled, isLoading, onSubmit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);

    // Auto-resize
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        160
      )}px`;
    }
  }, []);

  const canSend = value.trim().length > 0 && !disabled && !isLoading;

  return (
    <div className={cn('flex items-end gap-2', className)}>
      <div className="flex-1 relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || isLoading}
          placeholder={placeholder}
          rows={1}
          className={cn(
            'w-full resize-none',
            'min-h-[44px] max-h-[160px]',
            'py-3 px-4',
            'rounded-2xl border',
            'bg-background/80 border-border/60',
            'focus:outline-none focus:border-primary/80',
            'focus:shadow-[0_0_0_3px_hsl(220_70%_50%/0.15)]',
            'placeholder:text-muted-foreground/70',
            'text-sm text-foreground',
            'transition-all duration-240 ease-out',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
          aria-label="Reply message"
        />
      </div>

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={!canSend}
        size="icon"
        className={cn(
          'h-11 w-11 rounded-xl flex-shrink-0',
          'bg-primary hover:bg-primary/90',
          'text-primary-foreground',
          semanticTokens.motion.fast,
          semanticTokens.focus.ring,
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
        aria-label="Send reply"
      >
        {isLoading ? (
          <SFIcon icon={sfIcons.loader} className="h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          <SFIcon icon={sfIcons.send} className="h-5 w-5" aria-hidden="true" />
        )}
      </Button>
    </div>
  );
}
